import { useState, Fragment } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  HelpCircle,
  LayoutGrid,
  Paintbrush,
  Package,
  Plus,
  Upload,
  Loader2,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UnpublishedChangesBadge } from '@/components/creator/UnpublishedChangesBadge';
import { useDesignEntry } from '@/components/designs/DesignEntryChoice';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { embedLayout } from '@/lib/embed-layout';
import {
  useUIStore,
  useDesignStore,
  useProductSelectionStore,
  useAuthStore,
  isEmbedded,
  type CreatorStep,
} from '@/stores';
import {
  isForward,
  isLastStep as isLastOf,
  nextStep as nextOf,
  stepNumber,
  stepsForEntryMode,
} from '@/stores/creatorSteps';
import { PublishActionButton } from '../publish/PublishActionButton';
import { hasCreatorDesign } from '@/utils/hasCreatorDesign';
import { useGuidedEntryStore } from '@/stores/guidedEntryStore';
import { usePublishStore } from '@/stores/publishStore';
import { useSaveBeforeLeave } from './useSaveBeforeLeave';
import { SaveStatus } from './SaveStatus';
import i18n from '@/lib/i18n';
import { parseProductsFilterParam } from '@/lib/creatorProductsFilter';

/**
 * Block leaving a step whose work is not done yet. Returns true when the
 * navigation is allowed.
 *
 * - Design: a design has to exist before products or publishing.
 * - Catalogue (guided entry): at least one product must be picked before the
 *   products step, which shows only the picked ones — an empty step 3 would
 *   send the seller straight back. Going backwards is always allowed.
 */
function ensureStepCanBeLeft(
  steps: readonly CreatorStep[],
  currentStep: CreatorStep,
  target: CreatorStep
): boolean {
  if (currentStep === 'design') {
    if (hasCreatorDesign()) return true;
    // An embroidery motif only becomes a design once its threads are
    // confirmed; until then the step says what is missing, not "no motif".
    const guided = useGuidedEntryStore.getState();
    toast.error(
      i18n.t(
        steps.includes('catalog') && guided.procedure === null
          ? 'creator.toast.procedureRequired'
          : guided.hasUnconfirmedEmbroidery()
            ? 'creator.toast.threadsRequired'
            : 'creator.toast.designRequired'
      )
    );
    return false;
  }
  if (currentStep === 'catalog' && isForward(steps, currentStep, target)) {
    const picked = Object.values(
      useProductSelectionStore.getState().selections
    ).some((selection) => selection.isActive);
    if (picked) return true;
    toast.error(i18n.t('creator.toast.selectionRequired'));
    return false;
  }
  return true;
}

/**
 * Design step icon — droplet shape from the DesignDeck mockup (matches the
 * mockup's "goDesign" pill exactly). The accent dot reads --icon-accent so
 * it can switch to white on the filled/active pill (see StepIndicator).
 */
function DesignStepIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3 C 8 9, 5 12, 5 16 a 7 7 0 0 0 14 0 c 0 -4 -3 -7 -7 -13 z" />
      <circle
        cx="12"
        cy="16.5"
        r="2.25"
        fill="var(--icon-accent, #2b4cff)"
        stroke="none"
      />
    </svg>
  );
}

/**
 * Label key and icon per step. The sequence — and with it the numbering —
 * comes from stepsForEntryMode, so the classic entry stays at three steps
 * while the guided entry shows four.
 */
const STEP_META: Record<
  CreatorStep,
  { labelKey: string; icon: React.ReactNode }
> = {
  design: { labelKey: 'creator.header.steps.design', icon: <DesignStepIcon /> },
  catalog: {
    labelKey: 'creator.header.steps.catalog',
    icon: <LayoutGrid className="w-4 h-4" />,
  },
  products: {
    labelKey: 'creator.header.steps.products',
    icon: <Package className="w-4 h-4" />,
  },
  publish: {
    labelKey: 'creator.header.steps.publish',
    icon: <Upload className="w-4 h-4" />,
  },
};

/**
 * Step change shared by the step pills and "Weiter". The creator has two
 * save policies:
 *
 * - An existing design switches optimistically. `saveDesign` starts before
 *   the step changes: its synchronous prelude serialises the canvas and
 *   captures the thumbnail while the canvas is still mounted, and only the
 *   network chain runs on in the background. A failure lands in `syncError`
 *   and shows up in the header's SaveStatus, so nothing waits or spins here.
 * - A design without an id blocks. The route needs the id, so the first
 *   create (`saveDesign` delegates to `createDesign`) is awaited with a
 *   spinner; when it fails the seller stays on the step and sees a toast.
 *
 * Leaving the creator (useSaveBeforeLeave) and exiting single-product mode
 * (handleAddProducts) block as well, because both reload the design.
 *
 * While a publish runs (usePublishDesign) the step bar is locked: a switch
 * would start a save that could land after the status turned ACTIVE.
 *
 * `isCreating` is derived from the store, not kept per consumer: the step
 * pills and "Weiter" both call this hook, and a flag local to one of them
 * would leave the other enabled while the first create runs. A create in
 * flight is a save without a row yet (`isSaving && !designId`); the store
 * sets `isSaving` synchronously inside `createDesign`, before its first
 * await, so the lock is in place on the click tick.
 */
function useStepSwitch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setCurrentStep = useUIStore((state) => state.setCurrentStep);
  const setProductsStatusFilter = useUIStore(
    (state) => state.setProductsStatusFilter
  );
  const isPublishing = usePublishStore((state) => state.isPublishing);
  const isSaving = useDesignStore((state) => state.isSaving);
  const designId = useDesignStore((state) => state.designId);
  const isCreating = isSaving && !designId;

  const enterStep = (step: CreatorStep, targetDesignId: string) => {
    // A step starts unfiltered, unless the URL asks for a filter: the
    // overview opens a listing as `?filter=active`, and that has to hold
    // across the step buttons, not only on the first load.
    setProductsStatusFilter(
      parseProductsFilterParam(searchParams.get('filter')) ?? 'all'
    );
    setCurrentStep(step);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('new');
    nextParams.set('step', step);
    navigate(`/creator/${targetDesignId}?${nextParams.toString()}`, {
      replace: true,
    });
  };

  const switchStep = async (step: CreatorStep): Promise<void> => {
    // The buttons are disabled meanwhile; this guards programmatic callers.
    if (usePublishStore.getState().isPublishing) return;

    // Read at click time: a second click while a save is still running has
    // to see the current flags, not the ones the last render captured.
    const { designId, hasUnsavedChanges, saveDesign } =
      useDesignStore.getState();

    if (designId) {
      // No await on purpose (see above); saveDesign resolves to null on
      // failure instead of rejecting, so nothing is left unhandled.
      if (hasUnsavedChanges) void saveDesign();
      enterStep(step, designId);
      return;
    }

    // createDesign stores the new id as soon as the row exists, while the
    // pass is still writing crops and configuration. From then on the
    // design counts as existing, so the buttons unlock and a click switches
    // optimistically. This create must not navigate on top of that later.
    const stepAtClick = useUIStore.getState().currentStep;
    try {
      const created = await saveDesign();
      if (!created) {
        toast.error(t('creator.header.saveFailed'));
        return;
      }
      if (useUIStore.getState().currentStep !== stepAtClick) return;
      enterStep(step, created.id);
    } catch (error) {
      console.error('Creating the design on step change failed:', error);
      toast.error(t('creator.header.saveFailed'));
    }
  };

  return { switchStep, isCreating, isPublishing };
}

/**
 * Step Indicator component showing current progress (Prototype Style)
 *
 * A click saves the design in the background and switches at once; only the
 * first create of a design waits (see useStepSwitch).
 */
function StepIndicator({ currentStep }: { currentStep: CreatorStep }) {
  const { t } = useTranslation();
  const singleProductMode = useUIStore((state) => state.singleProductMode);
  const creatorEntryMode = useUIStore((state) => state.creatorEntryMode);
  const steps = stepsForEntryMode(creatorEntryMode);
  const { switchStep, isCreating, isPublishing } = useStepSwitch();
  const publishRunning = useDesignStore((state) => state.publishRunning);

  const handleStepClick = (stepId: CreatorStep) => {
    if (stepId === currentStep) return;
    if (!ensureStepCanBeLeft(steps, currentStep, stepId)) return;
    void switchStep(stepId);
  };

  // In single-product mode: only show the 'design' step
  const visibleSteps = (
    singleProductMode ? steps.filter((s) => s === 'design') : steps
  ).map((id) => ({ id, number: stepNumber(steps, id), ...STEP_META[id] }));

  // The reason the pills are locked; shown as a label and repeated as the
  // buttons' title so a hover on a pill explains itself too.
  const lockReason = isPublishing
    ? t('creator.header.saveStatus.publishing')
    : publishRunning
      ? t('creator.header.saveStatus.publishLocked')
      : undefined;

  return (
    <nav className="flex items-center gap-2">
      {visibleSteps.map((step, index) => {
        const isActive = currentStep === step.id;

        return (
          <Fragment key={step.id}>
            {/* A chevron between the steps reads as progress, not as a
                row of unrelated buttons. */}
            {index > 0 && (
              <ChevronRight
                className="h-4 w-4 flex-none text-[var(--mute)]"
                aria-hidden="true"
              />
            )}
            <button
              onClick={() => handleStepClick(step.id)}
              disabled={isCreating || isPublishing}
              title={lockReason}
              className={cn(
                'dc-step-tab flex items-center gap-2 h-10 px-4 rounded-control font-display font-semibold text-sm tracking-[-0.01em] transition-colors',
                isActive
                  ? 'bg-primary text-primary-fg'
                  : 'bg-transparent ring-1 ring-inset ring-[var(--line)] text-[var(--ink)] hover:bg-[var(--paper-2)]',
                (isCreating || isPublishing) && 'opacity-50 cursor-wait'
              )}
              style={
                isActive
                  ? ({ '--icon-accent': '#fff' } as React.CSSProperties)
                  : undefined
              }
            >
              {isCreating && step.id === currentStep ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                step.icon
              )}
              <span className="dc-step-number font-mono text-[10px] font-semibold tracking-[0.08em] opacity-65">
                {step.number}
              </span>
              {t(step.labelKey)}
            </button>
          </Fragment>
        );
      })}
      {/* Same typography as SaveStatus, so the lock reads as a status line. */}
      {lockReason && (
        <span
          aria-live="polite"
          className="dc-type-data ml-2 whitespace-nowrap text-[var(--mute)]"
        >
          {lockReason}
        </span>
      )}
    </nav>
  );
}

/**
 * Next Button - saves and advances to next step
 * On the last step (Publish), shows "Produkte erstellen" button
 */
function NextButton({ currentStep }: { currentStep: CreatorStep }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const singleProductMode = useUIStore((state) => state.singleProductMode);
  const saveDesign = useDesignStore((state) => state.saveDesign);
  const hasUnsavedChanges = useDesignStore((state) => state.hasUnsavedChanges);
  const designId = useDesignStore((state) => state.designId);
  const setCurrentStep = useUIStore((state) => state.setCurrentStep);
  const { switchStep, isCreating, isPublishing } = useStepSwitch();

  const creatorEntryMode = useUIStore((state) => state.creatorEntryMode);
  const steps = stepsForEntryMode(creatorEntryMode);
  const getNextStep = (): CreatorStep | null => nextOf(steps, currentStep);

  const handleNext = () => {
    const nextStep = getNextStep();
    if (!nextStep) return;
    if (!ensureStepCanBeLeft(steps, currentStep, nextStep)) return;
    void switchStep(nextStep);
  };

  const isLastStep = isLastOf(steps, currentStep);

  const setSingleProductMode = useUIStore(
    (state) => state.setSingleProductMode
  );

  // Blocking on purpose, unlike the step change above: leaving single-product
  // mode reloads the design with the master as canvas source, and a reload
  // during a running save would overwrite the edited product. Same policy as
  // leaving the creator (useSaveBeforeLeave).
  const handleAddProducts = async () => {
    setIsSaving(true);
    let newDesignId = designId;
    try {
      if (hasUnsavedChanges || !designId) {
        const savedDesign = await saveDesign();
        if (savedDesign) {
          newDesignId = savedDesign.id;
        }
      }
    } catch (error) {
      console.error('Save failed before returning to products:', error);
    }

    // Exit single-product mode BEFORE reloading so the master product
    // (not the edited product) is selected as the canvas source of truth.
    setSingleProductMode(null);

    setIsSaving(false);

    setCurrentStep('products');
    const nextParams = new URLSearchParams();
    nextParams.set('step', 'products');
    if (newDesignId) {
      navigate(`/creator/${newDesignId}?${nextParams.toString()}`, {
        replace: true,
      });
    } else {
      navigate(`/creator?${nextParams.toString()}`, { replace: true });
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="icon"
        className="rounded-control ring-1 ring-inset ring-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--paper-2)]"
        title={t('creator.header.help')}
      >
        <HelpCircle className="h-5 w-5" />
      </Button>

      {singleProductMode ? (
        <button
          onClick={handleAddProducts}
          disabled={isSaving}
          data-size="lg"
          className={cn(
            'dc-button flex items-center gap-2 h-[42px] px-[18px] rounded-control border border-primary bg-primary text-primary-fg font-display font-semibold text-[15px] tracking-[-0.01em] transition hover:brightness-[0.92]',
            isSaving && 'opacity-50 cursor-wait'
          )}
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          {t('creator.header.saveProducts')}
        </button>
      ) : isLastStep ? (
        <PublishActionButton inHeader />
      ) : (
        <button
          onClick={handleNext}
          disabled={isCreating || isPublishing}
          title={
            isPublishing ? t('creator.header.saveStatus.publishing') : undefined
          }
          data-size="lg"
          className={cn(
            'dc-button flex items-center gap-2 h-[42px] px-[18px] rounded-control border border-primary bg-primary text-primary-fg font-display font-semibold text-[15px] tracking-[-0.01em] transition hover:brightness-[0.92]',
            (isCreating || isPublishing) && 'opacity-50 cursor-wait'
          )}
        >
          {isCreating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {t('creator.header.next')}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Logo component (Prototype Style)
 *
 * A button rather than a link: leaving the creator saves the design first,
 * and only a button can wait for that save. The seller area is not meant to
 * open in a new tab (the creator runs in an iframe), so nothing is lost.
 */
function Logo({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy}
      aria-label={t('creator.header.toOverview')}
      className="flex items-center gap-2 disabled:cursor-wait"
    >
      <div className="w-8 h-8 bg-primary rounded-control flex items-center justify-center">
        <Paintbrush className="w-5 h-5 text-white" />
      </div>
      <span className="font-display font-bold text-xl tracking-[-0.01em] text-[var(--ink)]">
        DesignDeck
      </span>
    </button>
  );
}

/**
 * Header component - sticky header with navigation and actions (Prototype Style)
 */
export function Header() {
  const { t } = useTranslation();
  const currentStep = useUIStore((state) => state.currentStep);
  const { saveBeforeLeave, isLeaving } = useSaveBeforeLeave();
  const isIframe = useAuthStore((state) => state.isIframe);
  // Subscribed to `isIframe` above, so a flag change re-evaluates this.
  const embedded = isIframe && isEmbedded();
  const { hideBrand, hostChrome } = embedLayout();
  const creatorEntryMode = useUIStore((state) => state.creatorEntryMode);
  const { startGuided } = useDesignEntry();
  // Host-framed classic editor (reached through the guided entry's advanced
  // link): the way back to the guided entry, the host's home for designing.
  const showBackToGuided = hostChrome && creatorEntryMode === 'classic';

  // Wordmark and "Zurück" both leave for the design overview; unsaved work
  // is persisted first, and a failed save keeps the seller in the creator.
  const handleLeave = () => {
    void saveBeforeLeave('/designs');
  };

  return (
    <header className="dc-header h-16 bg-[var(--paper)] border-b border-[var(--line)] flex items-center px-6 shrink-0">
      {/* Left: Back + Logo. Inside a shop's iframe the wordmark alone leads
          to the design overview, so "back" would be a second way there; when
          the wordmark is hidden (the iframe default, lib/embed-layout.ts)
          "back" takes its place, so the overview, review and connections stay
          reachable. A host that owns the navigation gets neither. */}
      <div className="flex items-center gap-6">
        {!hostChrome && (!embedded || hideBrand) && (
          <button
            type="button"
            onClick={handleLeave}
            disabled={isLeaving}
            aria-busy={isLeaving}
            className="flex items-center gap-2 h-9 px-3 rounded-control text-[var(--ink)] font-display font-semibold text-sm hover:bg-[var(--paper-2)] transition disabled:cursor-wait"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
        )}
        {/* The Polaris embed sits inside the Shopify admin, which already
            names the app; a DesignDeck wordmark would read as a foreign site. */}
        {showBackToGuided && (
          <button
            type="button"
            onClick={() => void saveBeforeLeave(startGuided)}
            disabled={isLeaving}
            aria-busy={isLeaving}
            className="flex items-center gap-2 h-9 px-3 rounded-control text-[var(--ink)] font-display font-semibold text-sm hover:bg-[var(--paper-2)] transition disabled:cursor-wait"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('creator.header.backToGuided')}
          </button>
        )}
        {!hideBrand && <Logo onClick={handleLeave} busy={isLeaving} />}
        {/* Save state sits next to the wordmark, away from the step nav, so
            a status change reads as information rather than as a prompt. */}
        <SaveStatus />
      </div>

      {/* Center: Steps */}
      <div className="flex-1 flex justify-center">
        <StepIndicator currentStep={currentStep} />
      </div>

      {/* Right: unpublished-changes hint, then the actions */}
      <div className="flex items-center gap-3">
        <UnpublishedChangesBadge />
        <NextButton currentStep={currentStep} />
      </div>
    </header>
  );
}
